import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import {
  useAdjustStockMutation, useProductsQuery, useStockLedgerQuery, useUnitsQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, LoadingState,
  PageHeader, Pagination, Select, Table, Td, Th, Textarea,
} from '@/components/ui';
import { Can } from '@/components/Can';

/**
 * The reasons the API accepts, and what each one means for the sign of the
 * quantity. RESTOCK always adds and DAMAGE, LOSS and EXPIRY always remove —
 * the server forces the sign — so the form asks for a plain positive figure and
 * says which way it goes, rather than inviting a minus sign it would ignore.
 */
const REASONS = {
  ADJUSTMENT: {
    label: 'Correction',
    hint: 'Positive adds stock, negative removes it.',
    signed: true,
    inbound: false,
  },
  RESTOCK: {
    label: 'Restock',
    hint: 'Goods bought in without a purchase order. Always adds stock, and the '
      + 'unit cost you enter moves the product’s average cost.',
    signed: false,
    inbound: true,
  },
  DAMAGE: { label: 'Damage', hint: 'Always removes stock.', signed: false, inbound: false },
  LOSS: { label: 'Loss or theft', hint: 'Always removes stock.', signed: false, inbound: false },
  EXPIRY: { label: 'Expiry', hint: 'Always removes stock.', signed: false, inbound: false },
  OPENING: {
    label: 'Opening balance',
    hint: 'Stock you already held when you started using the system.',
    signed: true,
    inbound: false,
  },
} as const;

type Reason = keyof typeof REASONS;

interface LineDraft {
  variantId: string;
  quantity: string;
  unitId: string;
  unitCost: string;
  notes: string;
}

const emptyLine: LineDraft = { variantId: '', quantity: '', unitId: '', unitCost: '', notes: '' };

export function AdjustmentsPage() {
  const format = useFormat();
  const warehouses = useAppSelector((state) => state.tenant.context?.warehouses ?? []);

  // Arriving from "Restock" on the stock list lands on a form already pointed at
  // the product and warehouse that were on screen, so the reason for coming here
  // does not have to be re-entered.
  const [searchParams] = useSearchParams();
  const preset = searchParams.get('reason');

  const [warehouseId, setWarehouseId] = useState(searchParams.get('warehouseId') ?? '');
  const [reason, setReason] = useState<Reason>(
    preset && preset in REASONS ? (preset as Reason) : 'ADJUSTMENT',
  );
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([
    { ...emptyLine, variantId: searchParams.get('variantId') ?? '' },
  ]);
  const [failure, setFailure] = useState<string | null>(null);
  const [posted, setPosted] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: products } = useProductsQuery({ limit: 200, trackInventory: true });
  const { data: units } = useUnitsQuery({ limit: 100, isActive: true });
  const [adjustStock, adjustState] = useAdjustStockMutation();

  // The adjustment's own audit trail: the ledger is the record, so read it back
  // rather than keeping a second list of "adjustments" that could disagree.
  const history = useStockLedgerQuery({
    page,
    limit: 15,
    referenceType: 'STOCK_ADJUSTMENT',
    ...(warehouseId ? { warehouseId } : {}),
  });

  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name} — ${variant.sku}`,
      baseUnitId: product.baseUnitId,
    })),
  );

  const { signed, inbound } = REASONS[reason];
  const filled = lines.filter((line) => line.variantId && line.quantity !== '');
  const canPost = warehouseId !== '' && filled.length > 0;

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    setPosted(null);
    try {
      const result = await adjustStock({
        warehouseId,
        reason,
        notes: notes || undefined,
        lines: filled.map((line) => ({
          variantId: line.variantId,
          // Write-offs are sent as the magnitude; the server applies the sign.
          quantity: signed ? Number(line.quantity) : Math.abs(Number(line.quantity)),
          unitId: line.unitId || undefined,
          unitCost: line.unitCost ? Number(line.unitCost) : undefined,
          notes: line.notes || undefined,
        })),
      }).unwrap();

      setPosted(result.reference);
      setLines([{ ...emptyLine }]);
      setNotes('');
    } catch (error) {
      // Insufficient stock comes back as a 409 naming the product and balance.
      setFailure(apiErrorMessage(error, 'That adjustment could not be posted'));
    }
  };

  return (
    <>
      <PageHeader
        title="Stock adjustments"
        description="Restock, correct a balance, or write off damage, loss and expiry. Every line becomes a ledger entry."
      />

      <Can
        permission={P.STOCK_ADJUST}
        fallback={
          <Card>
            <EmptyState
              icon={SlidersHorizontal}
              title="You cannot post adjustments"
              description="Ask an administrator for the Adjust stock permission."
            />
          </Card>
        }
      >
        <Card className="mb-6">
          <CardHeader title="New adjustment" />

          <div className="space-y-4 p-4">
            {failure && <Alert tone="danger">{failure}</Alert>}
            {posted && (
              <Alert tone="success">
                Posted as <strong>{posted}</strong>. It is now in the stock ledger below.
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Warehouse"
                required
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
              >
                <option value="">Select a warehouse</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                ))}
              </Select>

              <Select
                label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value as Reason)}
                hint={REASONS[reason].hint}
              >
                {Object.entries(REASONS).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium">Lines</p>
              <div className="space-y-3">
                {lines.map((line, index) => (
                  <div key={index} className="flex flex-wrap items-end gap-3">
                    <div className="min-w-48 flex-1">
                      <Select
                        label={index === 0 ? 'Product' : undefined}
                        value={line.variantId}
                        onChange={(event) => {
                          const variant = variantOptions.find(
                            (option) => option.id === event.target.value,
                          );
                          setLine(index, {
                            variantId: event.target.value,
                            unitId: line.unitId || variant?.baseUnitId || '',
                          });
                        }}
                      >
                        <option value="">Select a product</option>
                        {variantOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </Select>
                    </div>

                    <div className="w-32">
                      <Input
                        label={
                          index === 0
                            ? signed
                              ? 'Quantity'
                              : inbound
                                ? 'Quantity in'
                                : 'Quantity out'
                            : undefined
                        }
                        type="number"
                        step="0.0001"
                        {...(signed ? {} : { min: '0' })}
                        value={line.quantity}
                        onChange={(event) => setLine(index, { quantity: event.target.value })}
                      />
                    </div>

                    <div className="w-32">
                      <Select
                        label={index === 0 ? 'Unit' : undefined}
                        value={line.unitId}
                        onChange={(event) => setLine(index, { unitId: event.target.value })}
                      >
                        <option value="">Base unit</option>
                        {units?.items.map((unit) => (
                          <option key={unit.id} value={unit.id}>{unit.symbol}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Cost only affects the moving average when stock comes IN. */}
                    {(inbound || (signed && Number(line.quantity) > 0)) && (
                      <div className="w-32">
                        <Input
                          label={index === 0 ? 'Unit cost' : undefined}
                          type="number"
                          min="0"
                          step="0.0001"
                          value={line.unitCost}
                          onChange={(event) => setLine(index, { unitCost: event.target.value })}
                        />
                      </div>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={lines.length === 1}
                      onClick={() =>
                        setLines((current) => current.filter((_, position) => position !== index))
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => setLines((current) => [...current, { ...emptyLine }])}
              >
                Add line
              </Button>
            </div>

            <Textarea
              label="Notes"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              hint="Why this adjustment was made. Kept on every line in the ledger."
            />

            <div className="flex justify-end">
              <Button
                loading={adjustState.isLoading}
                disabled={!canPost}
                onClick={() => void submit()}
              >
                Post adjustment
              </Button>
            </div>
          </div>
        </Card>
      </Can>

      <Card>
        <CardHeader
          title="Recent adjustments"
          description="Read back from the stock ledger — the adjustment's only record."
        />

        {history.isLoading ? (
          <LoadingState />
        ) : history.isError ? (
          <ErrorState
            message={apiErrorMessage(history.error)}
            onRetry={() => void history.refetch()}
          />
        ) : history.data && history.data.items.length === 0 ? (
          <EmptyState
            icon={SlidersHorizontal}
            title="No adjustments yet"
            description="Corrections and write-offs will appear here as they are posted."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Product</Th>
                  <Th>Warehouse</Th>
                  <Th>Type</Th>
                  <Th numeric>Quantity</Th>
                  <Th numeric>Balance after</Th>
                  <Th>Notes</Th>
                </tr>
              </thead>
              <tbody className={history.isFetching ? 'opacity-60' : undefined}>
                {history.data?.items.map((entry) => (
                  <tr key={entry.id}>
                    <Td className="text-secondary">{formatDateTime(entry.occurredAt, format)}</Td>
                    <Td>
                      <p>{entry.variant.product.name}</p>
                      <p className="text-xs text-muted">{entry.variant.sku}</p>
                    </Td>
                    <Td className="text-secondary">{entry.warehouse.name}</Td>
                    <Td>
                      <Badge tone={entry.direction === 'IN' ? 'success' : 'warning'}>
                        {entry.type.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </Td>
                    <Td
                      numeric
                      className={entry.direction === 'IN' ? 'text-emerald-600' : 'text-red-600'}
                    >
                      {entry.direction === 'IN' ? '+' : ''}
                      {formatQuantity(entry.quantity, format)} {entry.unit.symbol}
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatQuantity(entry.balanceAfter, format)}
                    </Td>
                    <Td className="text-secondary">{entry.notes ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {history.data && (
              <Pagination
                page={history.data.meta.page}
                totalPages={history.data.meta.totalPages}
                total={history.data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
}
